// Uhrzeit zum Eintippen (19.10.2026): die EINE Erkennung für alle Uhrzeitfelder im CRM (Komponente ZeitFeld).
// Reine Logik ohne React, damit sie mit Node getestet werden kann.

/** „15“, „1500“, „930“, „1530“, „15:30“, „15.30“, „9“, „15 Uhr“ → „HH:MM“; ungültig („25“, „1575“) → null */
export function zeitLesen(roh: string): string | null {
  const t = roh.trim().replace(/\s*uhr$/i, "");
  const m = t.match(/^(\d{1,2})(?:[:.,h ]?(\d{2}))?$/) ?? t.match(/^(\d)(\d{2})$/);
  if (!m) return null;
  const std = Number(m[1]);
  const min = m[2] === undefined ? 0 : Number(m[2]);
  if (std > 23 || min > 59) return null;
  return `${String(std).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

export interface ZeitBewertung {
  /** Eingabe darf übernommen werden (gültig, oder leer und leer erlaubt) */
  ok: boolean;
  /** übernommene Uhrzeit „HH:MM“ oder "" (leer); nur gesetzt, wenn ok */
  zeit: string;
  /** kurzer Grund, wenn nicht ok */
  grund: string | null;
}

/** Bewertet den Text eines Zeitfelds. `pruefen` liefert einen Grund, wenn die (gültige) Uhrzeit außerhalb eines Rahmens liegt. */
export function zeitBewerten(text: string, optionen: { leerErlaubt?: boolean; pruefen?: (zeit: string) => string | null } = {}): ZeitBewertung {
  const { leerErlaubt = true, pruefen } = optionen;
  if (text.trim() === "") return leerErlaubt ? { ok: true, zeit: "", grund: null } : { ok: false, zeit: "", grund: "Bitte eine Uhrzeit eintragen." };
  const z = zeitLesen(text);
  if (z === null) return { ok: false, zeit: "", grund: "Keine gültige Uhrzeit (z. B. 15 oder 15:30)." };
  const grund = pruefen?.(z) ?? null;
  if (grund) return { ok: false, zeit: "", grund };
  return { ok: true, zeit: z, grund: null };
}

/** Nächstes Auftreten einer getippten Uhrzeit (Ortszeit): heute, wenn sie noch kommt, sonst morgen. */
export function fristBisUhrzeit(zeit: string, jetzt = new Date()): string {
  const [h, m] = zeit.split(":").map(Number);
  const d = new Date(jetzt);
  d.setHours(h, m, 0, 0);
  if (d.getTime() <= jetzt.getTime()) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

function inMinuten(zeit: string): number {
  const [h, m] = zeit.split(":").map(Number);
  return h * 60 + m;
}

function ausMinuten(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

/** Uhrzeiten der Auswahlliste, z. B. 07:00 bis 20:00 in 15-Minuten-Schritten. `zusatz` (aktueller Wert außerhalb des Rasters) wird einsortiert. */
export function zeitOptionen(von = "07:00", bis = "20:00", schritt = 15, zusatz: string[] = []): string[] {
  const a = inMinuten(von);
  const b = inMinuten(bis);
  const menge = new Set<string>();
  for (let m = a; m <= b; m += Math.max(1, schritt)) menge.add(ausMinuten(m));
  for (const z of zusatz) if (/^\d{2}:\d{2}$/.test(z)) menge.add(z);
  return [...menge].sort();
}

/** Welcher Eintrag der Liste zum getippten Text gehört: genau die Zeit, sonst die nächste spätere (am Ende die letzte); ungültig/leer → null. */
export function zeitMarkierung(optionen: string[], text: string): string | null {
  const z = zeitLesen(text);
  if (z === null || optionen.length === 0) return null;
  return optionen.find((o) => o >= z) ?? optionen[optionen.length - 1];
}

/** Eintrag, zu dem die Liste beim Aufklappen scrollt: eingetragene Uhrzeit, sonst die nächste Viertelstunde ab `jetzt` („HH:MM“). */
export function zeitStart(optionen: string[], wert: string, jetzt: string): string | null {
  return zeitMarkierung(optionen, wert) ?? zeitMarkierung(optionen, jetzt);
}

/** Pfeiltaste: nächster (+1) oder vorheriger (-1) Eintrag, bleibt am Rand stehen. */
export function zeitSchritt(optionen: string[], aktuell: string | null, richtung: 1 | -1): string | null {
  if (optionen.length === 0) return null;
  const i = aktuell ? optionen.indexOf(aktuell) : -1;
  if (i < 0) return richtung === 1 ? optionen[0] : optionen[optionen.length - 1];
  return optionen[Math.max(0, Math.min(optionen.length - 1, i + richtung))];
}
